import { cssThemedValue } from '@creditornot/cb-ingredients';
import { RadioCard } from '@creditornot/cb-radio';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';

import { BaseField } from './types';
import { OverridableRowGroup } from '../../../constants';
import { useOfferOverridesContext } from '../../../context/OfferOverridesContext';

type ToggleProps = {
  value: boolean;
  onToggle: () => void;
};

const ToggleContainer = styled.div`
  display: flex;
  gap: 12px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-block-start: 1px solid ${cssThemedValue('borderPrimary')};
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
`;

const Title = styled(T.Body3)`
  font-weight: 700;
  width: 200px;
  padding-inline-end: 24px;
  flex-shrink: 0;
`;

export const Toggle = ({ value, onToggle }: ToggleProps) => {
  return (
    <ToggleContainer>
      <RadioCard
        onChange={onToggle}
        checked={!value}
        label={'Use shared menu'}
        subContent={'Automatically set by the shared menu'}
      />
      <RadioCard
        onChange={onToggle}
        checked={value}
        label={'Set my own'}
        subContent="Set manually, turn off automatic updates"
      />
    </ToggleContainer>
  );
};

type WithOverrideToggleProps = {
  children: React.ReactNode;
  title: string;
  sidePanelMode: BaseField['sidePanelMode'];
  rowGroup: OverridableRowGroup;
};

const TitleAndToggleContainer = styled.div`
  display: flex;
  margin-block-start: 16px;
`;

export const WithOverrideToggle = ({ children, title, sidePanelMode, rowGroup }: WithOverrideToggleProps) => {
  const menuPermissions = useOfferingMenuPermissionsContext();
  const { getRowGroupCanBeOverridden, getRowGroupHasOverride, setCurrOverriddenRowGroups } = useOfferOverridesContext();
  const rowContainsOverriddenField = getRowGroupHasOverride(rowGroup);
  const isRowGroupEnabledForOverride = getRowGroupCanBeOverridden(rowGroup);
  const [isOverrideToggleOn, setIsOverrideToggleOn] = useState(rowContainsOverriddenField);
  const showToggle = isRowGroupEnabledForOverride && sidePanelMode === 'edit';

  const onToggle = useCallback(() => {
    const willRevertToSharedMenuValue = isOverrideToggleOn;

    setIsOverrideToggleOn(prevState => !prevState);

    if (willRevertToSharedMenuValue) {
      setCurrOverriddenRowGroups(prevRowGroups => prevRowGroups.filter(prevRowGroup => prevRowGroup !== rowGroup));
      return;
    }

    setCurrOverriddenRowGroups(prevRowGroups => [...prevRowGroups, rowGroup]);
  }, [isOverrideToggleOn, rowGroup, setCurrOverriddenRowGroups]);

  const contextWithChildren = (
    <OverrideContextProvider value={{ isOverrideToggleOn, isRowGroupEnabledForOverride }}>
      {children}
    </OverrideContextProvider>
  );

  if (showToggle && menuPermissions.contentEdit) {
    return (
      <Container>
        <TitleAndToggleContainer>
          <Title>{title}</Title>
          <Toggle value={isOverrideToggleOn} onToggle={onToggle} />
        </TitleAndToggleContainer>
        {contextWithChildren}
      </Container>
    );
  }

  return contextWithChildren;
};

const DEFAULT_CONTEXT = {
  isOverrideToggleOn: false,
  isRowGroupEnabledForOverride: false,
};

const RowOverrideContext = React.createContext<typeof DEFAULT_CONTEXT>(DEFAULT_CONTEXT);

const OverrideContextProvider = RowOverrideContext.Provider;

export const useRowOverrideContext = () => React.useContext(RowOverrideContext);
